import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn } from "../../ui";

/**
 * Correcting one block of what the model wrote down.
 *
 * ONE component for both halves - the summary and the verbatim transcript - on
 * purpose. They are the same affordance with different text in them, and built
 * separately they would drift into two answers to "what does cancel do", "what
 * does an emptied box mean" and "when is the save button busy".
 *
 * An explicit edit with a save button, like `CallNote` next door and for the
 * same reason: reading a call log is a browsing activity, and a field that
 * writes itself while somebody scrolls past it is a field that eats text by
 * accident. Unlike the note, there is no delete button - clearing the box is
 * the way to say "there is none", and the server reads a blank as null.
 */
export function CallTextEdit({
  value,
  editing,
  onEditing,
  onSave,
  placeholder,
  /** Shown under the box while the draft would do something surprising. */
  warn,
  rows = 6,
}: {
  value: string | null;
  editing: boolean;
  onEditing: (v: boolean) => void;
  onSave: (value: string | null) => Promise<void>;
  placeholder?: string;
  warn?: (draft: string) => string | null;
  rows?: number;
}) {
  const t = useT();
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  // Opening the editor, or switching to another call, starts from what is
  // actually stored rather than from the last thing typed anywhere.
  useEffect(() => {
    if (editing) setDraft(value ?? "");
  }, [editing, value]);

  if (!editing) return null;

  // Computed on the draft rather than on what was saved: the point is to say so
  // BEFORE the button is pressed, while it is still one keystroke to undo.
  const warning = warn?.(draft) ?? null;

  async function commit() {
    setBusy(true);
    try {
      await onSave(draft.trim() ? draft : null);
      onEditing(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="crm-ai-edit">
      <textarea
        autoFocus
        rows={rows}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        className="crm-note-ta"
      />
      {warning ? <p className="crm-ai-warn">{warning}</p> : null}
      <div className="mt-2 flex items-center gap-2">
        <Btn small tone="dark" onClick={() => void commit()} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {t("common.save")}
        </Btn>
        <Btn small onClick={() => onEditing(false)} disabled={busy}>
          {t("common.cancel")}
        </Btn>
      </div>
    </div>
  );
}
